import type { PolygonWithHoles, Ring, Vec2 } from '../geo/types';
import type { Channel } from '../data/types';
import { fromRing } from '../geo/clip';
import { extrude, rayCrossing, rayToRim, rimBetween, type RimPoint } from './farRim';

const PERIMETER_STEP_M = 250;
// Only stretches that no site edge lies along are probed, a step inside the ring.
const PROBE_INSET_M = 0.01;
const ON_RING_M = 1e-6;

// A channel the bounds cut across, from its end inside them along the direction of its last stretch.
export interface FarRingCrossing {
  readonly channel: Channel;
  readonly from: Vec2;
  readonly direction: Vec2;
}

export interface FarRingSeam {
  readonly water: readonly PolygonWithHoles[];
  readonly land: readonly PolygonWithHoles[];
  readonly crossings: readonly FarRingCrossing[];
}

// A piece of far-ring water with the channel it carries on, if any, and a point a step inside the
// bounds on the stretch it continues.
export interface FarWaterRun {
  readonly polygon: PolygonWithHoles;
  readonly channel: Channel | null;
  readonly probe: Vec2;
}

export interface FarRing {
  readonly land: readonly PolygonWithHoles[];
  readonly water: readonly PolygonWithHoles[];
  readonly waterRuns: readonly FarWaterRun[];
}

const NO_SEAM: FarRingSeam = { water: [], land: [], crossings: [] };

interface Interval {
  readonly from: number;
  readonly to: number;
}

interface Side {
  readonly a: Vec2;
  readonly b: Vec2;
  readonly length: number;
  readonly offset: number;
}

interface Stop {
  readonly p: Vec2;
  readonly s: number;
}

interface Run {
  readonly inner: readonly Stop[];
  readonly wet: boolean;
  readonly crossing: FarRingCrossing | null;
}

function sidesOf(ring: Ring): readonly Side[] {
  let offset = 0;
  return ring.map((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const side = { a, b, length, offset };
    offset += length;
    return side;
  });
}

// Arc length from the ring's first corner to p, or null when p is not on the ring.
function arcPosition(sides: readonly Side[], p: Vec2): number | null {
  for (const side of sides) {
    const s = positionOn(side, p);
    if (s !== null) return s;
  }
  return null;
}

// Arc length to p along one side, or null when p is not on that side.
function positionOn({ a, b, length, offset }: Side, p: Vec2): number | null {
  const dx = (b[0] - a[0]) / length;
  const dy = (b[1] - a[1]) / length;
  const along = (p[0] - a[0]) * dx + (p[1] - a[1]) * dy;
  const across = (p[0] - a[0]) * dy - (p[1] - a[1]) * dx;
  const onSide =
    Math.abs(across) <= ON_RING_M && along >= -ON_RING_M && along <= length + ON_RING_M;
  return onSide ? offset + Math.max(0, Math.min(length, along)) : null;
}

function sortedStops(stops: readonly Stop[]): readonly Stop[] {
  const sorted = [...stops].sort((x, y) => x.s - y.s);
  return sorted.filter((stop, i) => i === 0 || stop.s - sorted[i - 1]!.s > ON_RING_M);
}

function ringsOf(polygons: readonly PolygonWithHoles[]): readonly Ring[] {
  return polygons.flatMap((polygon) => [polygon.outer, ...polygon.holes]);
}

// The corners, plus every vertex the site already has on the bounds, so both sides of the seam
// share the same vertices.
function seamStops(sides: readonly Side[], seam: FarRingSeam): readonly Stop[] {
  const vertices = ringsOf([...seam.water, ...seam.land]).flat();
  return sortedStops(
    [...sides.map((side) => side.a), ...vertices].flatMap((p) => {
      const s = arcPosition(sides, p);
      return s === null ? [] : [{ p, s }];
    })
  );
}

// The stretches of the ring that the polygons' own edges lie along, measured on the side that holds
// each edge, so an edge that ends at a corner keeps its place on that side.
function edgesAlong(sides: readonly Side[], polygons: readonly PolygonWithHoles[]): Interval[] {
  return ringsOf(polygons).flatMap((ring) =>
    ring.flatMap((a, i) => {
      const b = ring[(i + 1) % ring.length]!;
      const middle: Vec2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const side = sides.find((sd) => positionOn(sd, middle) !== null);
      const [s, t] = side ? [positionOn(side, a), positionOn(side, b)] : [null, null];
      return s !== null && t !== null && s !== t
        ? [{ from: Math.min(s, t), to: Math.max(s, t) }]
        : [];
    })
  );
}

function sideAt(sides: readonly Side[], s: number): Side {
  return sides.find((side) => s <= side.offset + side.length) ?? sides[sides.length - 1]!;
}

function within(s: number, intervals: readonly Interval[]): boolean {
  return intervals.some(({ from, to }) => s > from - ON_RING_M && s < to + ON_RING_M);
}

// PLAN §5 step 5 splits each side every 250 m; those points shape the far rim only.
function rimStops(sides: readonly Side[]): readonly Stop[] {
  return sides.flatMap(({ a, b, length, offset }) => {
    const steps = Math.max(1, Math.round(length / PERIMETER_STEP_M));
    return Array.from({ length: steps }, (_, k): Stop => {
      const t = k / steps;
      return { p: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t], s: offset + length * t };
    });
  });
}

// A step inside the ring from the point at arc position s.
function probeAt(sides: readonly Side[], s: number): Vec2 {
  const side = sideAt(sides, s);
  const t = (s - side.offset) / side.length;
  const inward: Vec2 = [
    -(side.b[1] - side.a[1]) / side.length,
    (side.b[0] - side.a[0]) / side.length,
  ];
  return [
    side.a[0] + (side.b[0] - side.a[0]) * t + inward[0] * PROBE_INSET_M,
    side.a[1] + (side.b[1] - side.a[1]) * t + inward[1] * PROBE_INSET_M,
  ];
}

function runPolygon(
  inner: readonly Stop[],
  start: RimPoint,
  end: RimPoint,
  rim: readonly RimPoint[]
): PolygonWithHoles {
  return fromRing([
    ...inner.map((stop) => stop.p),
    end.p,
    ...rimBetween(rim, start.s, end.s),
    start.p,
  ]);
}

// A stretch takes the kind of the site edge that lies along it: a probe inside can land in the
// water where a bank leaves the bounds at a shallow angle, a few centimetres from land.
function wetRuns(
  sides: readonly Side[],
  stops: readonly Stop[],
  ends: readonly Stop[],
  seam: FarRingSeam,
  isWaterFn: (p: Vec2) => boolean
): readonly boolean[] {
  const [wet, dry] = [edgesAlong(sides, seam.water), edgesAlong(sides, seam.land)];
  return stops.map((stop, i) => {
    const middle = (stop.s + ends[i]!.s) / 2;
    if (within(middle, wet)) return true;
    if (within(middle, dry)) return false;
    return isWaterFn(probeAt(sides, middle));
  });
}

// The arc position where a crossing channel's centreline, carried on, meets the ring.
function exitPosition(sides: readonly Side[], { from, direction }: FarRingCrossing): number | null {
  let position: number | null = null;
  let nearest = Infinity;
  for (const side of sides) {
    const crossing = rayCrossing(from, direction, side.a, side.b);
    if (crossing && crossing.t < nearest) {
      nearest = crossing.t;
      position = side.offset + side.length * crossing.u;
    }
  }
  return position;
}

// Runs break wherever the kind changes, and at the ring's first corner.
function runsOf(
  sides: readonly Side[],
  stops: readonly Stop[],
  ends: readonly Stop[],
  wet: readonly boolean[],
  crossings: readonly FarRingCrossing[]
): readonly Run[] {
  const exits = crossings.map((crossing) => ({ crossing, s: exitPosition(sides, crossing) }));
  const starts = stops.flatMap((_, i) => (i === 0 || wet[i] !== wet[i - 1] ? [i] : []));
  return starts.map((start, j) => {
    const end = starts[j + 1] ?? stops.length;
    const inner = [...stops.slice(start, end), ends[end - 1]!];
    const [first, last] = [inner[0]!.s, inner[inner.length - 1]!.s];
    const exit = wet[start]
      ? exits.find(({ s }) => s !== null && s >= first && s <= last)
      : undefined;
    return { inner, wet: wet[start]!, crossing: exit?.crossing ?? null };
  });
}

// The rim point each run boundary runs out to: beside a river carried on past the bounds, along the
// river's own direction, so the band keeps its width; radially everywhere else.
function boundaryRims(
  runs: readonly Run[],
  rim: readonly RimPoint[],
  center: Vec2,
  farRadius: number
): readonly RimPoint[] {
  const lastRun = runs[runs.length - 1]!.inner;
  const boundaries = [...runs.map((run) => run.inner[0]!), lastRun[lastRun.length - 1]!];
  const rims = boundaries.map((stop, k): RimPoint => {
    const crossing = k > 0 && k < runs.length ? (runs[k]!.crossing ?? runs[k - 1]!.crossing) : null;
    return crossing
      ? rayToRim(rim, stop.p, crossing.direction)
      : { p: extrude(stop.p, center, farRadius), s: stop.s };
  });
  const crossed = rims.findIndex((point, k) => k > 0 && point.s < rims[k - 1]!.s);
  if (crossed !== -1) {
    throw new Error(
      `Far ring: the run boundary at ${boundaries[crossed]!.p.join(', ')} crosses the one before it`
    );
  }
  return rims;
}

function waterRunOf(run: Run, polygon: PolygonWithHoles, sides: readonly Side[]): FarWaterRun {
  const middle = (run.inner[0]!.s + run.inner[run.inner.length - 1]!.s) / 2;
  return { polygon, channel: run.crossing?.channel ?? null, probe: probeAt(sides, middle) };
}

// PLAN §5 step 5: runs of land or water along the bounds, carried out to the far radius. Runs break
// where the site's own outline meets the bounds, so each run continues exactly what lies inside it,
// and the seam has no vertex or kind on one side that the other lacks. A river the bounds cut runs
// on straight along its own direction at its own width; everything else is carried out radially,
// and the land is what the water leaves of the ring.
export function buildFarRing(
  boundsRing: Ring,
  isWaterFn: (p: Vec2) => boolean,
  farRadius: number,
  seam: FarRingSeam = NO_SEAM
): FarRing {
  const sides = sidesOf(boundsRing);
  const perimeter = sides.reduce((sum, side) => sum + side.length, 0);
  const stops = seamStops(sides, seam);
  const closing: Stop = { p: stops[0]!.p, s: perimeter };
  const ends = [...stops.slice(1), closing];
  const center: Vec2 = [
    (boundsRing[0]![0] + boundsRing[2]![0]) / 2,
    (boundsRing[0]![1] + boundsRing[2]![1]) / 2,
  ];
  const rim = sortedStops([...stops, ...rimStops(sides), closing]).map((stop): RimPoint => ({
    p: extrude(stop.p, center, farRadius),
    s: stop.s,
  }));
  const runs = runsOf(
    sides,
    stops,
    ends,
    wetRuns(sides, stops, ends, seam, isWaterFn),
    seam.crossings
  );
  const rims = boundaryRims(runs, rim, center, farRadius);
  const polygons = runs.map((run, k) => runPolygon(run.inner, rims[k]!, rims[k + 1]!, rim));
  const waterRuns = runs.flatMap((run, k) =>
    run.wet ? [waterRunOf(run, polygons[k]!, sides)] : []
  );
  return {
    land: polygons.filter((_, k) => !runs[k]!.wet),
    water: waterRuns.map((run) => run.polygon),
    waterRuns,
  };
}
